
import React from 'react'
import { ArrowLeft, BrainCircuit, ExternalLink, Scale, Clock, Tag, DollarSign } from 'lucide-react'
import { cn } from '@/lib/utils'
import { modelColor } from './library-types'
import type { CatalogModel } from './library-types'
import { Toggle } from './library-cards'

interface ModelDetailViewProps {
  model: CatalogModel
  isInLibrary: boolean
  onToggleLibrary: (modelId: string) => Promise<void>
  onBack: () => void
  onAddToCanvas?: () => void
}

export function ModelDetailView({ model, isInLibrary, onToggleLibrary, onBack, onAddToCanvas }: ModelDetailViewProps) {
  const bg = modelColor(model.id)
  const authorName = model.modelFamily ?? model.provider?.name ?? model.modelFamilyId ?? 'Unknown'

  return (
    <div className="flex flex-col h-full">
      {/* Back button */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to catalog
        </button>
      </div>

      <div className="flex gap-6 flex-1 min-h-0">
        {/* Left: Image */}
        <div className="w-[360px] flex-shrink-0">
          <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-background">
            {model.thumbnailUrl ? (
              <img
                src={model.thumbnailUrl}
                alt={model.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div
                className="h-full w-full flex items-center justify-center"
                style={{ backgroundColor: bg }}
              >
                <BrainCircuit className="h-16 w-16 text-black/15" strokeWidth={1} />
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex gap-2">
            {onAddToCanvas && (
              <button
                onClick={onAddToCanvas}
                className="flex-1 h-9 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
              >
                Open tool
              </button>
            )}
            <div className="flex items-center gap-2 px-3 h-9 rounded-lg border border-border">
              <span className="text-xs text-muted-foreground">Show in Node Controls</span>
              <Toggle checked={isInLibrary} onChange={() => void onToggleLibrary(model.id)} />
            </div>
          </div>
        </div>

        {/* Right: Details */}
        <div className="flex-1 min-w-0 overflow-y-auto pr-1">
          {/* Title */}
          <h2 className="text-xl font-semibold leading-tight">{model.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">{authorName}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {model.purpose && (
              <span className="inline-flex items-center gap-1 bg-muted border border-border h-6 px-2 rounded-lg text-xs">
                {model.purpose}
              </span>
            )}
            {model.nodeType && (
              <span className="inline-flex items-center gap-1 bg-muted border border-border h-6 px-2 rounded-lg text-xs">
                {model.nodeType}
              </span>
            )}
            {model.category && (
              <span className="inline-flex items-center gap-1 bg-muted border border-border h-6 px-2 rounded-lg text-xs">
                {model.category}
              </span>
            )}
          </div>

          {/* Description */}
          {model.description && (
            <div className="mt-5">
              <h3 className="text-sm font-medium mb-1.5">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {model.description}
              </p>
            </div>
          )}

          {/* Details grid */}
          <div className="mt-5 grid grid-cols-2 gap-3">
            {model.provider && (
              <DetailItem
                icon={<ExternalLink className="h-3.5 w-3.5" />}
                label="Provider"
                value={model.provider.name}
              />
            )}
            {model.endpointId && (
              <DetailItem
                icon={<Tag className="h-3.5 w-3.5" />}
                label="Endpoint"
                value={model.endpointId}
                mono
              />
            )}
            {model.licenseType && (
              <DetailItem
                icon={<Scale className="h-3.5 w-3.5" />}
                label="License"
                value={model.licenseType}
              />
            )}
            {model.durationEstimate && (
              <DetailItem
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Est. Duration"
                value={`${model.durationEstimate}s`}
              />
            )}
          </div>

          {/* Pricing */}
          {model.pricing && model.pricing.originalPrice != null && model.pricing.originalPrice > 0 && (
            <div className="mt-5">
              <h3 className="text-sm font-medium mb-2">Pricing</h3>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
                <DollarSign className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-lg font-semibold leading-tight">
                    ${model.pricing.originalPrice.toFixed(model.pricing.originalPrice < 0.01 ? 4 : 2)}
                    <span className="text-sm font-normal text-muted-foreground ml-1">
                      {model.pricing.currency || 'USD'}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    per {formatPricingUnit(model.pricing.unit)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Capabilities */}
          {model.capabilities && model.capabilities.length > 0 && (
            <div className="mt-5">
              <h3 className="text-sm font-medium mb-2">Capabilities</h3>
              <div className="flex flex-wrap gap-1.5">
                {model.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="inline-flex items-center bg-background border border-border h-6 px-2 rounded-lg text-xs text-muted-foreground"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function formatPricingUnit(unit?: string): string {
  const labels: Record<string, string> = {
    image: 'image',
    video: 'video',
    per_image: 'image',
    per_video: 'video',
    per_second: 'second',
    per_megapixel: 'megapixel',
    per_request: 'request',
  }
  return labels[unit || ''] || unit || 'request'
}

function DetailItem({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-muted/50">
      <span className="text-muted-foreground mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={cn('text-sm truncate', mono && 'font-mono text-xs')}>{value}</p>
      </div>
    </div>
  )
}
